import * as React from "react";
import { useCallback, useEffect, useRef } from "react";
import styled from "styled-components";

import { setTimeoutIfPossible } from "@faire/retailer/lib/async-functions/setTimeoutIfPossible";

import { HeaderItemCss } from "./styled";

interface IProps {
  children?: React.ReactNode | undefined;
  className?: string;
  hoverChild: React.ReactNode;
  onHover?: () => void;
  onBlur?: () => void;
  noHoverExtension?: boolean;
  keepHiddenAfterClick?: boolean;
  dataTestId?: string;
  ariaLabel?: string;
}

interface HeaderHoverItemElement extends HTMLDivElement {
  reveal?: () => void;
  handleBlur?: () => void;
}

export const HeaderHoverItem: React.FunctionComponent<IProps> = ({
  keepHiddenAfterClick,
  onBlur,
  onHover,
  noHoverExtension,
  children,
  dataTestId,
  ariaLabel,
  hoverChild,
  className,
}) => {
  const containerRef = useRef<HeaderHoverItemElement | null>(null);
  const timeoutInstance = useRef<number | undefined>(undefined);
  const [hideHover, setHideHover] = React.useState(false);
  const [firstHoverHappened, setFirstHoverHappened] = React.useState(false);
  const [hideHoverExtension, setHideHoverExtension] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);

  const clearTimeoutInstances = useCallback(() => {
    if (timeoutInstance.current) {
      clearTimeout(timeoutInstance.current);
      timeoutInstance.current = undefined;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearTimeoutInstances();
    };
  }, [clearTimeoutInstances]);

  const handleHeaderClick = useCallback(() => {
    clearTimeoutInstances();
    if (keepHiddenAfterClick) {
      setHideHover(true);
    }
  }, [clearTimeoutInstances, keepHiddenAfterClick]);

  const handleChildClick = useCallback(() => {
    setHideHoverExtension(false);
    setFirstHoverHappened(false);
  }, []);

  const handleHoverExtensionClickOrChildMouseEnter = useCallback(() => {
    setHideHoverExtension(true);
  }, []);

  const handleChildMouseLeave = useCallback(() => {
    setHideHoverExtension(false);
  }, []);

  const reveal = useCallback(() => {
    onHover?.();
    setFirstHoverHappened(true);
    if (keepHiddenAfterClick) {
      setHideHover(false);
    }
  }, [keepHiddenAfterClick, onHover]);

  const handleHover = useCallback(
    (e: React.SyntheticEvent<HTMLDivElement>) => {
      setHideHoverExtension(false);
      setIsExpanded(true);

      timeoutInstance.current = setTimeoutIfPossible(() => {
        setHideHoverExtension(true);
      }, 1000);

      if (e.currentTarget === containerRef.current) {
        reveal();
      }
    },
    [reveal]
  );

  const handleBlur = useCallback(() => {
    setIsExpanded(false);
    onBlur?.();
  }, [onBlur]);

  const refCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) {
        return;
      }
      containerRef.current = node;
      containerRef.current.reveal = reveal;
      containerRef.current.handleBlur = handleBlur;
    },
    [handleBlur, reveal]
  );

  return (
    <HeaderHoverItemContainer
      className={className}
      $hideHoverExtension={hideHoverExtension}
      ref={refCallback}
      onMouseEnter={handleHover}
      onFocus={handleHover}
      onMouseOut={handleBlur}
      onBlur={handleBlur}
      onClick={handleHeaderClick}
      data-test-id={dataTestId}
      role="link"
      aria-haspopup="true"
      aria-expanded={isExpanded}
      aria-label={ariaLabel}
    >
      {noHoverExtension ? (
        <HeaderItem>{children}</HeaderItem>
      ) : (
        <HoverExtension onClick={handleHoverExtensionClickOrChildMouseEnter}>
          {children}
        </HoverExtension>
      )}

      {firstHoverHappened ? (
        <HoverChild
          onMouseEnter={handleHoverExtensionClickOrChildMouseEnter}
          onMouseLeave={handleChildMouseLeave}
          onClick={handleChildClick}
        >
          {!hideHover ? hoverChild : null}
        </HoverChild>
      ) : null}
    </HeaderHoverItemContainer>
  );
};

export const HeaderItem = styled.div`
  ${HeaderItemCss}
`;

const HoverChild = styled.div``;

const HoverExtension = styled.div`
  ${HeaderItemCss};
  position: relative;
  padding: 0;
  width: 32px;

  &::before {
    display: none;
    content: "";
    height: 100%;
    top: 0;
    position: absolute;

    width: calc(100% + 60px);
    left: -30px;
  }
`;

// Using a button here broke hover behaviour on firefox
const HeaderHoverItemContainer = styled.div<{ $hideHoverExtension?: boolean }>`
  ${HoverChild} {
    display: none;
  }

  &:hover,
  &:focus-within {
    ${HoverExtension} {
      &::before {
        display: ${(props) => (props.$hideHoverExtension ? "none" : "flex")};
      }
    }

    ${HoverChild} {
      display: block;
    }
  }
`;
